import { useSyncExternalStore } from 'react';

// 04 #95: device-side theme state. The AUTHORITY for the current mode is the `dark`
// class on <html>: index.html sets it before first paint from localStorage
// ('aralere-theme'), this store toggles it at runtime, and every themed value in
// global.css keys off `:root.dark`. There is deliberately NO server field and NO
// schema: the choice is a device preference (the 04 #95 lock). Absence of the key
// means light; there is no OS prefers-color-scheme auto-detection (owner-ruled at
// the s104 prototype: default light unless toggled).

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'aralere-theme';
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

function currentTheme(): ThemeMode {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Storage unavailable (private mode): the choice still applies for this session.
  }
  notify();
}

export function toggleTheme(): void {
  applyTheme(currentTheme() === 'dark' ? 'light' : 'dark');
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** The current mode as React state; re-renders subscribers on every toggle. */
export function useTheme(): ThemeMode {
  return useSyncExternalStore(subscribe, currentTheme, () => 'light');
}

// Cross-tab sync: a toggle in another tab of the same portal updates this one live.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    document.documentElement.classList.toggle('dark', e.newValue === 'dark');
    notify();
  });
}
